import React, { FC, useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import fr from 'dayjs/locale/fr';
import { rrulestr } from 'rrule';

type PlanningRecurrence = PlanningDayJson & {
  rrule?: string | null;
  exdates?: string[] | null;
  date_planning?: string;
  source?: PlanningDayJson;
};
import { Empty, PullToRefresh, Space } from 'antd-mobile';
import { LeftOutline, RightOutline } from 'antd-mobile-icons';
import { PullStatus } from 'antd-mobile/es/components/pull-to-refresh';
import { useNavigate } from 'react-router-dom';

import { useNavbar } from '../../../hooks/use-navbar';
import { apiClient } from '../../../api';
import { PlanningDayJson, UserJson, UserPresence } from '../../../types/models';

dayjs.locale(fr);
dayjs.extend(isoWeek);

export interface TypePlanningAgent {
  year: number;
  week: number;
  days: PlanningDayJson[];
  agent: UserJson;
}

interface InterventionJsonLike {
  timestamp: string;
  author_id?: number;
  client_id?: number;
}

// Le backend renvoie le record de base (date = DTSTART) pour les plannings
// recurrents et ne genere PAS les occurrences. Le frontend doit les expanser
// en respectant la RRULE et les exdates (cf. canonical algo de la doc API).
//
// Pour chaque planning :
// - Si pas de RRULE -> garde tel quel
// - Si RRULE       -> drop le record de base, regenere des occurrences
//                     virtuelles dans [from, to] (excluant les exdates).
//                     L occurrence du DTSTART garde l id original, les autres
//                     ont id=null (virtuelles, read-only).
function expandRecurrences(
  days: PlanningRecurrence[],
  fromDate: Date,
  toDate: Date,
): PlanningRecurrence[] {
  return days.flatMap((day) => {
    if (!day.rrule) {
      return [day];
    }
    try {
      const rule = rrulestr(day.rrule);
      const exdates = new Set(day.exdates ?? []);
      const occurs = rule
        .between(fromDate, toDate, true)
        .map((d) => dayjs(d).format('YYYY-MM-DD'))
        .filter((d) => !exdates.has(d));
      return occurs.map((d) => ({
        ...day,
        date: d,
        date_planning: d,
        id: d === day.date ? day.id : (null as unknown as number),
        source: day,
      }));
    } catch (e) {
      // Si la RRULE ne se parse pas, on garde le record tel quel (fail-safe)
      return [day];
    }
  });
}

const Planning: FC = () => {
  const navigate = useNavigate();
  const navbar = useNavbar();
  const [mainDay, setDay] = useState<Dayjs>(dayjs().startOf('isoWeek'));
  const [weekPlanning, setWeekPlanning] = useState<TypePlanningAgent>();
  const [interventionCounts, setInterventionCounts] = useState<Map<string, number>>(new Map());
  const initialScrollDone = useRef(false);
  const refreshInterval = 1000 * 60 * 5; // 5 minutes

  useEffect(() => {
    if (navbar) {
      navbar.setTitle('Planning');
      navbar.setBackArrow(false);
      navbar.setRight(
        <div style={{ fontSize: 24 }}>
          <Space style={{ '--gap': '16px' }}>
            <LeftOutline
              onClick={() => {
                setDay((mainDay) => mainDay.subtract(1, 'week'));
              }}
            />
            <RightOutline
              onClick={() => {
                setDay((mainDay) => mainDay.add(1, 'week'));
              }}
            />
          </Space>
        </div>,
      );
    }
  }, []);

  const loadPlanning = async () => {
    try {
      // Get start and end of the week
      const from = mainDay.startOf('isoWeek').format('YYYY-MM-DD');
      const to = mainDay.endOf('isoWeek').format('YYYY-MM-DD');

      const response = await apiClient.planning.getPlanning({
        from,
        to,
      });

      // Process the planning data
      const raw: PlanningDayJson[] = response;
      console.log('raw planning', raw);

      // Expansion des plannings recurrents en occurrences virtuelles dans
      // la plage [from, to] avant le regroupement par jour de la semaine.
      const fromDate = mainDay.startOf('isoWeek').toDate();
      const toDate = mainDay.endOf('isoWeek').toDate();
      const expanded = expandRecurrences(raw as PlanningRecurrence[], fromDate, toDate);

      const result: TypePlanningAgent[] = expanded.reduce((acc, aDay) => {
        // Day of week (Mon=0, Tue=1, ..., Sun=6) — semaine ISO commence lundi
        const dow = dayjs(aDay.date).isoWeekday() - 1;

        if (!acc[aDay.agent.id]) {
          // First planning for this agent - create empty week structure
          const days: PlanningDayJson[] = [...Array(7)].map((_, i) => {
            const dayDate = mainDay.startOf('isoWeek').add(i, 'day');
            return {
              agent: aDay.agent as UserJson,
              dow: i,
              year: dayDate.year(),
              week: dayDate.isoWeek(),
              date: dayDate.format('YYYY-MM-DD'),
              startAt: null,
              endAt: null,
              comment: null,
              id: 0,
              clients: [],
              presence: UserPresence.WORKING,
            };
          });

          acc[aDay.agent.id] = {
            year: mainDay.year(),
            week: mainDay.isoWeek(),
            days: days,
            agent: aDay.agent as UserJson,
          };
        }

        // Merge planning data for this day
        const existingDay = acc[aDay.agent.id].days[dow];

        // Merge clients (avoid duplicates)
        const existingClientIds = new Set(existingDay.clients.map(c => c.id));
        const newClients = aDay.clients.filter(c => !existingClientIds.has(c.id));
        const mergedClients = [...existingDay.clients, ...newClients];

        // Merge comments
        const comments = [existingDay.comment, aDay.comment].filter(Boolean);
        const mergedComment = comments.length > 0 ? comments.join(' | ') : null;

        // Merge time ranges (keep earliest start and latest end)
        const mergedStartAt = existingDay.startAt && aDay.startAt
          ? (existingDay.startAt < aDay.startAt ? existingDay.startAt : aDay.startAt)
          : existingDay.startAt || aDay.startAt;

        const mergedEndAt = existingDay.endAt && aDay.endAt
          ? (existingDay.endAt > aDay.endAt ? existingDay.endAt : aDay.endAt)
          : existingDay.endAt || aDay.endAt;

        // Update the day with merged data
        acc[aDay.agent.id].days[dow] = {
          ...aDay,
          dow,
          clients: mergedClients,
          comment: mergedComment,
          startAt: mergedStartAt,
          endAt: mergedEndAt,
          id: existingDay.id || aDay.id, // Keep first non-zero id
        };

        return acc;
      }, [] as TypePlanningAgent[]);

      console.log('refetch planning', { weekPlanning: result });
      setWeekPlanning(result.pop() as TypePlanningAgent);
    } catch (error) {
      console.error('Error loading planning:', error);
    }
  };

  useEffect(() => {
    loadPlanning();
  }, [mainDay]);

  // Fetch les interventions de chaque client present dans le planning
  // pour la semaine affichee, et indexe par date+clientId
  useEffect(() => {
    if (!weekPlanning) {
      return;
    }
    const fromMs = mainDay.startOf('isoWeek').valueOf();
    const toMs = mainDay.endOf('isoWeek').valueOf();
    const clientIds = new Set<number>();
    weekPlanning.days.forEach((d) => d.clients.forEach((c) => clientIds.add(c.id)));
    if (clientIds.size === 0) {
      setInterventionCounts(new Map());
      return;
    }

    const fetches = Array.from(clientIds).map((cid) =>
      apiClient.interventions
        .getInterventions({ filter: `client_id||eq||${cid}`, limit: 5000 })
        .then((res) => ({ cid, ivs: res.data }))
        .catch(() => ({ cid, ivs: [] as InterventionJsonLike[] })),
    );
    Promise.all(fetches).then((results) => {
      const counts = new Map<string, number>();
      results.forEach(({ cid, ivs }) => {
        ivs.forEach((iv) => {
          if (!iv.timestamp) {
            return;
          }
          const m = dayjs(iv.timestamp);
          const t = m.valueOf();
          if (t < fromMs || t > toMs) {
            return;
          }
          const dateStr = m.format('YYYY-MM-DD');
          const key = `${dateStr}__${cid}`;
          counts.set(key, (counts.get(key) || 0) + 1);
        });
      });
      setInterventionCounts(counts);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekPlanning, mainDay]);

  useEffect(() => {
    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(loadPlanning, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval]);

  const daysOfWeek = [
    'Lundi ' + mainDay.add(0, 'day').format('DD/MM'),
    'Mardi ' + mainDay.add(1, 'day').format('DD/MM'),
    'Mercredi ' + mainDay.add(2, 'day').format('DD/MM'),
    'Jeudi ' + mainDay.add(3, 'day').format('DD/MM'),
    'Vendredi ' + mainDay.add(4, 'day').format('DD/MM'),
    'Samedi ' + mainDay.add(5, 'day').format('DD/MM'),
    'Dimanche ' + mainDay.add(6, 'day').format('DD/MM'),
  ];

  const todayStr = dayjs().format('YYYY-MM-DD');
  const isCurrentWeek = mainDay.isSame(dayjs().startOf('isoWeek'), 'day');

  useEffect(() => {
    if (initialScrollDone.current) {
      return;
    }
    if (!weekPlanning || !isCurrentWeek) {
      return;
    }
    const el = document.getElementById('planning-day-today');
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }, 80);
      initialScrollDone.current = true;
    }
  }, [weekPlanning, isCurrentWeek]);

  return (
    <div style={{ paddingBottom: 12 }}>
      <div
        style={{
          margin: '12px 12px 0',
          padding: '12px 14px',
          background: 'rgba(230,57,70,0.12)',
          border: '1px solid rgba(230,57,70,0.45)',
          borderLeft: '4px solid #E63946',
          borderRadius: 12,
          color: 'var(--text-primary)',
          fontSize: 12.5,
          lineHeight: 1.5,
        }}
      >
        <div
          style={{
            fontWeight: 800,
            marginBottom: 4,
            color: '#FF8A95',
            textTransform: 'uppercase',
            fontSize: 10.5,
            letterSpacing: '0.06em',
          }}
        >
          ⚠ À signaler immédiatement
        </div>
        Tout retard, changement ou annulation doit être remonté immédiatement au responsable d&rsquo;exploitation{' '}
        <b>Franck Doiteaux</b> —{' '}
        <a href="tel:0699560640" style={{ color: '#31DCC8', fontWeight: 700, textDecoration: 'none' }}>
          06 99 56 06 40
        </a>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'baseline',
          gap: 8,
          margin: '14px 12px 10px',
          color: 'var(--text-muted)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        <span>Semaine {mainDay.isoWeek()}</span>
        <span style={{ color: 'rgba(248,250,251,0.25)' }}>·</span>
        <span style={{ color: 'var(--text-primary)', letterSpacing: '0.02em', textTransform: 'none', fontSize: 13 }}>
          {mainDay.format('DD MMM')} — {mainDay.endOf('isoWeek').format('DD MMM YYYY')}
        </span>
      </div>

      <PullToRefresh
        renderText={(status: PullStatus) => {
          const map: Record<PullStatus, string> = {
            pulling: 'Tirer pour rafraîchir',
            canRelease: 'Relâcher pour rafraîchir',
            refreshing: 'Mise à jour…',
            complete: 'À jour',
          };
          return <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: 6 }}>{map[status]}</div>;
        }}
        onRefresh={async () => {
          await loadPlanning();
        }}
      >
      {weekPlanning ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 12px' }}>
          {weekPlanning.days.map((day) => {
            const isToday = day.date === todayStr;
            const isPast = dayjs(day.date).isBefore(dayjs().startOf('day'));
            const isFuture = dayjs(day.date).isAfter(dayjs(), 'day');
            const hasClients = day.clients.length > 0;
            const totalClients = day.clients.length;
            const clientsDone = day.clients.filter(
              (c) => (interventionCounts.get(`${day.date}__${c.id}`) || 0) > 0,
            ).length;
            const pct = totalClients > 0 ? Math.round((clientsDone / totalClients) * 100) : 0;
            const allDone = totalClients > 0 && clientsDone === totalClients;

            // "Lundi 08/06" -> split en jour + date
            const dayLabel = daysOfWeek[day.dow];
            const dayName = dayLabel.split(' ')[0];
            const dayDate = dayLabel.split(' ')[1];

            const heroBg = isToday
              ? 'linear-gradient(135deg, rgba(49,220,200,0.18), rgba(49,220,200,0.06))'
              : isPast
                ? 'var(--bg-card)'
                : 'var(--bg-card)';
            const cardBorder = isToday
              ? '2px solid rgba(49,220,200,0.6)'
              : '1px solid var(--border-subtle)';
            const cardShadow = isToday
              ? '0 8px 24px rgba(49,220,200,0.12)'
              : 'var(--shadow-card)';

            return (
              <div
                key={day.dow}
                id={isToday ? 'planning-day-today' : undefined}
                style={{
                  scrollMarginTop: 12,
                  background: 'var(--bg-card)',
                  border: cardBorder,
                  borderRadius: 16,
                  overflow: 'hidden',
                  opacity: isPast && !isToday ? 0.55 : 1,
                  boxShadow: cardShadow,
                }}
              >
                {/* ========== HERO HEADER ========== */}
                <div
                  style={{
                    padding: '14px 14px 12px',
                    background: heroBg,
                    borderBottom: hasClients
                      ? '1px solid var(--border-subtle)'
                      : 'none',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                    }}
                  >
                    {/* Gros chiffre nb clients */}
                    <div
                      style={{
                        minWidth: 52,
                        height: 52,
                        borderRadius: 14,
                        background: isToday
                          ? '#31DCC8'
                          : hasClients
                            ? 'rgba(49,220,200,0.16)'
                            : 'rgba(127,127,127,0.12)',
                        color: isToday
                          ? '#0B1628'
                          : hasClients
                            ? 'var(--teal)'
                            : 'var(--text-faint)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                    >
                      <span style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em' }}>
                        {totalClients}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          marginTop: 3,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          opacity: 0.85,
                        }}
                      >
                        clt{totalClients > 1 ? 's' : ''}
                      </span>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isToday && (
                        <div
                          style={{
                            display: 'inline-block',
                            fontSize: 9,
                            fontWeight: 800,
                            color: '#0B1628',
                            background: '#31DCC8',
                            padding: '2px 7px',
                            borderRadius: 100,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            marginBottom: 4,
                          }}
                        >
                          Aujourd&rsquo;hui
                        </div>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 20,
                            fontWeight: 900,
                            color: isToday ? 'var(--teal)' : 'var(--text-primary)',
                            letterSpacing: '-0.025em',
                            textTransform: 'capitalize',
                            lineHeight: 1.1,
                          }}
                        >
                          {dayName}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                          }}
                        >
                          {dayDate}
                        </span>
                      </div>
                      {(day.startAt || day.endAt) && (
                        <div
                          style={{
                            fontSize: 11.5,
                            color: 'var(--text-muted)',
                            marginTop: 3,
                            fontWeight: 500,
                          }}
                        >
                          🕒{' '}
                          {day.startAt
                            ? day.startAt.split(':').splice(0, 2).join('h')
                            : '-'}{' '}
                          → {day.endAt ? day.endAt.split(':').splice(0, 2).join('h') : '-'}
                        </div>
                      )}
                    </div>

                    {/* Chip progression */}
                    {hasClients && !isFuture && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: 2,
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 900,
                            color: allDone ? '#10B981' : 'var(--text-primary)',
                            letterSpacing: '-0.01em',
                          }}
                        >
                          {clientsDone}/{totalClients}
                        </span>
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: allDone ? '#10B981' : 'var(--text-muted)',
                          }}
                        >
                          {allDone ? '✓ Saisi' : 'Saisis'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Barre de progression */}
                  {hasClients && (
                    <div
                      style={{
                        marginTop: 12,
                        height: 5,
                        background: 'rgba(127,127,127,0.18)',
                        borderRadius: 100,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${isFuture ? 0 : pct}%`,
                          height: '100%',
                          background: isFuture
                            ? 'transparent'
                            : allDone
                              ? '#10B981'
                              : 'linear-gradient(90deg, var(--teal), #10B981)',
                          borderRadius: 100,
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* ========== BODY ========== */}
                {!hasClients && (
                  <div
                    style={{
                      padding: '16px 14px',
                      fontSize: 12.5,
                      color: 'var(--text-faint)',
                      fontStyle: 'italic',
                      textAlign: 'center',
                    }}
                  >
                    Aucun client planifié
                  </div>
                )}

                {hasClients && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {day.clients.map((client, idx) => {
                      const ivCount =
                        interventionCounts.get(`${day.date}__${client.id}`) || 0;
                      const done = ivCount > 0;
                      const futureDay = dayjs(day.date).isAfter(dayjs(), 'day');

                      // Pastille : pleine si concerné (aujourd hui ou passé),
                      // creuse si futur — distingue "j'ai du travail" vs "a venir"
                      const pinStyle: React.CSSProperties = futureDay
                        ? {
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          border: '2px solid var(--text-faint)',
                          background: 'transparent',
                        }
                        : {
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: done ? '#10B981' : '#E63946',
                          boxShadow: done
                            ? '0 0 0 3px rgba(16,185,129,0.22)'
                            : '0 0 0 3px rgba(230,57,70,0.22)',
                        };

                      return (
                        <button
                          key={`${client.id}-${idx}`}
                          onClick={() => navigate(`/client/${client.id}`)}
                          style={{
                            appearance: 'none',
                            background: 'transparent',
                            border: 'none',
                            borderTop:
                              idx === 0 ? 'none' : '1px solid var(--border-subtle)',
                            color: 'var(--text-primary)',
                            fontFamily: 'inherit',
                            textAlign: 'left',
                            width: '100%',
                            padding: '13px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            cursor: 'pointer',
                            WebkitTapHighlightColor: 'rgba(49,220,200,0.15)',
                          }}
                        >
                          <span
                            aria-label={
                              futureDay ? 'Programmé' : done ? 'Saisi' : 'Non saisi'
                            }
                            style={{ ...pinStyle, flexShrink: 0, marginLeft: 2 }}
                          />
                          <span
                            style={{
                              fontSize: 15,
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              lineHeight: 1.35,
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              opacity: futureDay ? 0.75 : 1,
                            }}
                          >
                            {client.name || client.display_name}
                          </span>
                          {ivCount > 0 && (
                            <span
                              style={{
                                background: '#10B981',
                                color: '#fff',
                                padding: '4px 10px',
                                borderRadius: 100,
                                fontSize: 11.5,
                                fontWeight: 800,
                                letterSpacing: 0.01,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 2px 6px rgba(16,185,129,0.32)',
                              }}
                            >
                              {ivCount} ✓
                            </span>
                          )}
                          {!futureDay && ivCount === 0 && (
                            <span
                              style={{
                                background: '#E63946',
                                color: '#fff',
                                padding: '4px 10px',
                                borderRadius: 100,
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: 0.04,
                                textTransform: 'uppercase',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 2px 6px rgba(230,57,70,0.32)',
                              }}
                            >
                              À saisir
                            </span>
                          )}
                          <span
                            style={{
                              color: 'var(--text-faint)',
                              fontSize: 16,
                              flexShrink: 0,
                              lineHeight: 1.4,
                            }}
                          >
                            ›
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <Empty />
      )}
      </PullToRefresh>
    </div>
  );
};

export default Planning;
